import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Typography,
  MenuItem,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { getUniqueCurrencies } from "../../utils/currencyOptions";

const CollectPaymentDialog = ({
  open,
  onClose,
  customer,
  token,
  onSuccess,
}) => {
  const { t } = useTranslation();

  const {
    handleAddAsync: handleBalancePaymentAddAsync,
    loading: balancePaymentLoading,
  } = useAdd("shop/customer-balance-payments", token);
  const [transferPaymentLoading, setTransferPaymentLoading] = useState(false);

  const { data: currenciesData, isLoading: currencyLoading } = useFetchObjects(
    ["currency"],
    "shop/currencies",
    token
  );

  const currencies = currenciesData?.results ?? currenciesData ?? [];
  const customerShopId = customer?.shop ?? customer?.shop_details?.id ?? "";
  
  // Filter currencies to only show those belonging to the customer's shop
  const filteredCurrencies = getUniqueCurrencies(
    currencies.filter((c) => !customerShopId || String(c.shop) === String(customerShopId))
  );

  const { data: accountsData, isLoading: accountLoading } = useFetchObjects(
    ["accounts", "collect-payment", customerShopId],
    customerShopId ? `shop/accounts/?page_size=all&shop=${customerShopId}` : "",
    token
  );
  const accounts = Array.isArray(accountsData) ? accountsData : accountsData?.results || [];

  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [discount, setDiscount] = useState("");
  const [exchangeRate, setExchangeRate] = useState("1");
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().slice(0, 16)
  );
  const [notes, setNotes] = useState("");

  const resetForm = () => {
    setSelectedCurrency(null);
    setSelectedAccountId("");
    setAmount("");
    setDiscount("");
    setExchangeRate("1");
    setPaymentDate(new Date().toISOString().slice(0, 16));
    setNotes("");
  };

  useEffect(() => {
    if (!open) resetForm();
  }, [open]);

  const isTransferGroup = customer?.recordType === "stock-transfer-group";
  const isSubmitting = balancePaymentLoading || transferPaymentLoading;

  const handleSubmit = async () => {
    if (!selectedCurrency || !selectedAccountId || !amount) {
      alert("Please fill all required fields.");
      return;
    }

    try {
      if (isTransferGroup) {
        setTransferPaymentLoading(true);
        const account = accounts.find((acc) => String(acc.id) === String(selectedAccountId));
        if (!account) throw new Error("Selected account not found.");
        
        const transferIds = customer?.items
          ?.map((item) => item?.sourceTransfer?.transfer_id)
          .filter(Boolean) || [];
        const metadata = `[Transfers: ${transferIds.join(", ")}]`;
        
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/shop/account-transactions/`,
          {
            account: Number(selectedAccountId),
            transaction_currency: Number(selectedCurrency.id),
            transaction_type: "credit",
            amount: Number(amount),
            exchange_rate: Number(exchangeRate) || 1,
            description: `${notes.trim()}${notes.trim() ? " " : ""}${metadata}`.trim(),
            transaction_date: paymentDate.slice(0, 10),
          },
          {
            headers: { Authorization: `Token ${token}` },
          }
        );
      } else {
        if (!customer?.id) throw new Error("Customer ID is missing.");
        
        await handleBalancePaymentAddAsync({
          customer: Number(customer.id),
          account: Number(selectedAccountId),
          currency: Number(selectedCurrency.id),
          amount: Number(amount),
          discount: Math.max(0, Number(discount) || 0),
          exchange_rate: Number(exchangeRate) || 1,
          apply_to_opening_balance: true,
          payment_date: paymentDate,
          notes: notes.trim() || undefined,
        });
      }

      resetForm();
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Payment error details:", error);
      const backendErrors = error.response?.data;
      let errorMsg = "Failed to record payment.";
      
      if (backendErrors && typeof backendErrors === "object") {
        const errorList = Object.entries(backendErrors).map(([key, value]) => {
          const field = key.charAt(0).toUpperCase() + key.slice(1);
          const message = Array.isArray(value) ? value.join(", ") : value;
          return `${field}: ${message}`;
        });
        errorMsg = errorList.join("\n") || errorMsg;
      } else {
        errorMsg = error.message || errorMsg;
      }
      
      alert('Payment Error:\n' + errorMsg);
    } finally {
      setTransferPaymentLoading(false);
    }
  };

  const currentBalance = customer?.total_owed_by_currency;
  const balanceStr =
    currentBalance && typeof currentBalance === "object"
      ? Object.entries(currentBalance)
          .map(([cur, amt]) => `${Number(amt).toFixed(2)} ${cur}`)
          .join(", ") || "0"
      : "0";

  const discountValue = Math.max(0, Number(discount) || 0);
  const amountValue = Number(amount) || 0;
  const totalReducePreview =
    Number.isFinite(amountValue) && amountValue > 0
      ? (amountValue + discountValue).toFixed(2)
      : "";

  const remainingBalance = React.useMemo(() => {
    if (!selectedCurrency || !amount || isNaN(Number(amount))) return null;
    const curTitle = selectedCurrency.title;
    const currentAmt = Number(currentBalance?.[curTitle]) || 0;
    if (currentAmt === 0) return null;
    const remaining = currentAmt - Number(amount) - discountValue;
    return {
      value: remaining.toFixed(2),
      currency: curTitle
    };
  }, [selectedCurrency, amount, currentBalance, discountValue]);

  if (!customer) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t("customer.collectPayment", "Collect payment")} – {customer.name}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t("salePayment.currentBalance", "Current balance")}
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {balanceStr}
            </Typography>
            
            {remainingBalance && (
              <Box sx={{ mt: 1, pt: 1, borderTop: "1px dashed", borderColor: "grey.400" }}>
                <Typography variant="subtitle2" color="primary.main" gutterBottom>
                  {t("salePayment.remainingBalance", "Remaining balance after payment")}
                </Typography>
                <Typography variant="h6" color="primary.main" fontWeight="bold">
                  {remainingBalance.value} {remainingBalance.currency}
                </Typography>
              </Box>
            )}

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {t("customer.payAgainstBalanceHelp", "Recording a payment will reduce this balance.")}
            </Typography>
          </Box>

          <TextField
            select
            SelectProps={{ native: true }}
            margin="dense"
            label={t("salePayment.selectCurrency", "Currency")}
            fullWidth
            required
            value={selectedCurrency?.id ?? ""}
            onChange={(e) => {
              const c = filteredCurrencies.find((x) => String(x.id) === e.target.value);
              setSelectedCurrency(c || null);
            }}
            disabled={currencyLoading}
          >
            <option value="" />
            {(filteredCurrencies || []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </TextField>
          <TextField
            select
            margin="dense"
            label={t("salePayment.account", "Account")}
            fullWidth
            required
            value={selectedAccountId}
            onChange={(e) => setSelectedAccountId(e.target.value)}
            disabled={accountLoading}
          >
            {accounts.map((acc) => (
              <MenuItem key={acc.id} value={String(acc.id)}>
                {acc.name} ({acc.currency_title || "-"})
              </MenuItem>
            ))}
          </TextField>

          <TextField
            margin="dense"
            label={t("salePayment.amount", "Amount")}
            type="number"
            fullWidth
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ min: 0, step: "0.01" }}
            helperText={t(
              "salePayment.amountHelp",
              "Cash received from customer"
            )}
          />
          {!isTransferGroup && (
            <>
              <TextField
                margin="dense"
                label={t("salePayment.discount", "Discount")}
                type="number"
                fullWidth
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                inputProps={{ min: 0, step: "0.01" }}
                helperText={t(
                  "salePayment.discountHelp",
                  "Extra amount forgiven from customer balance (not cash)"
                )}
              />
              {totalReducePreview && (
                <Typography variant="body2" color="text.secondary">
                  {t("salePayment.totalBalanceReduce", "Total balance reduce")}:{" "}
                  <strong>
                    {totalReducePreview}
                    {selectedCurrency?.title ? ` ${selectedCurrency.title}` : ""}
                  </strong>
                  {" "}
                  ({t("salePayment.amount", "Amount")}
                  {discountValue > 0
                    ? ` + ${t("salePayment.discount", "Discount")}`
                    : ""}
                  )
                </Typography>
              )}
            </>
          )}
          <TextField
            margin="dense"
            label={t("salePayment.exchangeRate", "Exchange Rate")}
            type="number"
            fullWidth
            required
            value={exchangeRate}
            onChange={(e) => setExchangeRate(e.target.value)}
            inputProps={{ min: 0, step: "0.000001" }}
          />
          <TextField
            margin="dense"
            label={t("salePayment.paymentDate", "Payment date")}
            type="datetime-local"
            fullWidth
            required
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("salePayment.notes", "Notes")}
            multiline
            rows={2}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={isSubmitting}>
          {t("customer.cancel", "Cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isSubmitting || !selectedCurrency || !selectedAccountId || !amount}
          sx={{ minWidth: 100 }}
        >
          {isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("customer.recordPayment", "Record payment")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CollectPaymentDialog;
